/**
 * Shared chrome for the public /privacy and /terms pages — same header
 * (Wordmark + theme toggle) and card frame as Home/Login (see ../landing.css
 * for why Tailwind is scoped + "tw:"-prefixed here). The prose itself is
 * plain CSS (.legal-prose in index.css), not Tailwind — see that rule for why.
 */
import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { useTheme } from "../hooks/useTheme";
import { Wordmark } from "./Wordmark";
import { ThemeToggle } from "./ThemeToggle";
import "../landing.css";

export function LegalPage({
  title,
  updated,
  children,
}: {
  title: string;
  /** e.g. "July 27, 2026" — plain text, not parsed. */
  updated: string;
  children: ReactNode;
}) {
  const [theme, toggleTheme] = useTheme();

  return (
    <div className="tw:bg-white tw:dark:bg-[#0f1115] tw:text-neutral-800 tw:dark:text-[#e6e8ee] tw:font-sans tw:min-h-screen">
      <div className="tw:fixed tw:top-6 tw:inset-x-6 tw:z-50 tw:flex tw:justify-between tw:items-center">
        <Link
          to="/"
          className="tw:no-underline tw:bg-white tw:dark:bg-[#171a21] tw:border tw:border-transparent tw:dark:border-[#2a2f3a] tw:pl-4 tw:pr-5 tw:py-2 tw:rounded-full tw:shadow-md"
        >
          <Wordmark size="2.25rem" />
        </Link>
        <ThemeToggle theme={theme} onToggle={toggleTheme} />
      </div>

      <div className="tw:max-w-3xl tw:mx-auto tw:px-6 tw:pt-32 tw:pb-24">
        <div className="tw:bg-white tw:dark:bg-[#1e222b] tw:p-8 tw:md:p-14 tw:rounded-3xl tw:shadow-xl tw:border tw:border-neutral-100 tw:dark:border-[#2a2f3a]">
          <h1 className="tw:text-3xl tw:md:text-4xl tw:font-medium tw:tracking-tight tw:mb-2">{title}</h1>
          <p className="tw:text-sm tw:text-neutral-400 tw:dark:text-[#6b7280] tw:mb-8">
            Last updated {updated}
          </p>
          <div className="legal-prose">{children}</div>
        </div>

        <p className="tw:text-center tw:text-sm tw:mt-8">
          <Link
            to="/"
            className="tw:no-underline tw:text-[#e06d10] tw:dark:text-[#fb923c] tw:hover:underline"
          >
            &larr; Back to quelby
          </Link>
        </p>
      </div>
    </div>
  );
}
